import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Coins, TrendUp, Receipt, CreditCard, ChartLineUp, Warning } from "@phosphor-icons/react";

/**
 * Shows a compact "financial pulse" row for a single project.
 * Revenue booked + AR outstanding + costs + margin + budget usage.
 */
export default function ProjectFinanceChip({ projectId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/finance/project-snapshot/${projectId}`);
        if (active) setData(data);
      } catch { /* silent */ }
    })();
    return () => { active = false; };
  }, [projectId]);

  if (!data) {
    return (
      <div className="dc-card p-3 text-[10px] font-mono-alt uppercase tracking-widest text-slate-400" data-testid={`project-finance-loading-${projectId}`}>
        Loading financials…
      </div>
    );
  }

  const revenueTotal = (data.revenue.booked_fe || 0) + (data.revenue.invoiced_ar || 0);
  const marginPct = data.margin.pct;
  const budgetUsed = data.budget_used_pct;
  const marginTone = marginPct == null ? "text-slate-700"
    : marginPct < 0 ? "text-red-700"
    : marginPct < 10 ? "text-amber-700"
    : "text-emerald-700";
  const budgetTone = budgetUsed == null ? "text-slate-600"
    : budgetUsed > 100 ? "text-red-700"
    : budgetUsed > 85 ? "text-amber-700"
    : "text-emerald-700";

  const nothing = revenueTotal === 0 && data.costs.total === 0;

  return (
    <div className="dc-card p-3" data-testid={`project-finance-chip-${projectId}`}>
      <div className="mb-2 flex items-center gap-2">
        <ChartLineUp size={12} className="text-[#1E5CBA]" weight="fill" />
        <span className="overline">Financial snapshot</span>
        {nothing && (
          <span className="ml-auto font-mono-alt text-[9px] uppercase tracking-widest text-slate-400">
            no finance activity yet
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5 text-xs">
        <Cell icon={TrendUp} iconTone="text-emerald-600" label="Contract" value={formatCurrency(data.contract_value)} />
        <Cell icon={Receipt} iconTone="text-emerald-600" label="Invoiced"
              value={formatCurrency(data.revenue.invoiced_ar)}
              sub={data.revenue.outstanding > 0 ? `${formatCurrency(data.revenue.outstanding)} outstanding` : null} />
        <Cell icon={CreditCard} iconTone="text-red-600" label="Costs"
              value={formatCurrency(data.costs.total)}
              sub={data.costs.outstanding_ap > 0 ? `${formatCurrency(data.costs.outstanding_ap)} AP pending` : null} />
        <Cell icon={Coins} iconTone="text-blue-600" label="Margin"
              value={marginPct == null ? "—" : `${marginPct}%`}
              sub={formatCurrency(data.margin.gross)}
              valueClass={marginTone} />
        <Cell
          icon={budgetUsed != null && budgetUsed > 100 ? Warning : ChartLineUp}
          iconTone={budgetTone}
          label="Budget"
          value={budgetUsed == null ? "—" : `${budgetUsed}%`}
          sub={data.budget_total ? `of ${formatCurrency(data.budget_total)}` : "no budget set"}
          valueClass={budgetTone}
        />
      </div>
    </div>
  );
}

function Cell({ icon: Icon, iconTone, label, value, sub, valueClass = "text-slate-900" }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50/60 px-2 py-1.5">
      <div className="flex items-center gap-1">
        <Icon size={10} className={iconTone} weight="fill" />
        <span className="overline">{label}</span>
      </div>
      <div className={`mt-0.5 font-mono-alt tabular-nums font-semibold ${valueClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 truncate">{sub}</div>}
    </div>
  );
}
